import { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { GetSurvey, GetTrackingProgress, SaveSurveyResults } from '../../../services/SurveyService'
import { SurveyLinkContext, type SurveyLinkContextValue } from './SurveyLinkContext'
import { type UploadFilesEvent, type Model, type Question, type QuestionPanelDynamicModel } from 'survey-core'
import { registerCustomQuestions } from './questions'
import { SurveyDisplay } from './SurveyDisplay'
import { SurveyNavigationButtons } from './SurveyNavigationButtons'
import { type SurveySchema, SurveyType } from '../../../models/SurveySchema'
import { UploadFile } from '../../../services/FileService'
import { Box, CircularProgress, Fade, Typography } from '@mui/material'
import { SurveyStepper } from './SurveyStepper'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { SURVEY_COMMENT_SUFFIX, SURVEY_FILE_KEY_SUFFIX, SURVEY_FILE_SUFFIX } from '../../../constants/surveyConstants'

import 'survey-core/survey-core.min.css'
import './survey-overrides.css'

registerCustomQuestions()

const AUTO_SAVE_DELAY = 1000

interface QuestionWithMetadata extends Question {
  uploadedFileName?: string
  uploadedFileKey?: string
  panels?: Array<{
    questions: QuestionWithMetadata[]
  }>
}

function isPanelDynamicQuestion (question: Question): question is QuestionPanelDynamicModel {
  return question.getType() === 'paneldynamic' || question.getType() === 'surveypaneldynamic'
}

function hasMetadata (question: Question): question is QuestionWithMetadata {
  return 'uploadedFileName' in question || 'uploadedFileKey' in question
}

interface Props {
  annualPlanId: number
  surveyType: SurveyType
  title?: string
  navigateToSummary?: () => void
  planOrgId: number
  onSurveyLoaded?: (survey: SurveySchema) => void
  initialSurvey?: SurveySchema
  setSurvey?: Dispatch<SetStateAction<SurveySchema | undefined>>
  onExit?: () => void
  submitModalTitle?: string
  surveyVariables?: Record<string, any>
  /**
   * Optional persistence overrides. When provided, the shared SurveyJS display /
   * autosave machinery is reused unchanged, but load and save are routed to a
   * consumer-specific store (e.g. Peer-to-Peer's per-building form) instead of the
   * default SurveyService (which keys on surveyType + planOrgId).
   */
  loadOverride?: () => Promise<SurveySchema | null>
  saveOverride?: (answersJson: string, submit?: boolean) => Promise<void>
  /**
   * Renders the survey read-only (SurveyJS `display` mode) — e.g. the P2P management
   * correction dialog viewing a submitted form. Optional; existing consumers unaffected.
   */
  readOnly?: boolean
  /**
   * Per-survey SurveyJS theme variables, merged over the transparent-background base.
   * Lets each survey carry its own colour scheme; omit to keep SurveyJS defaults
   * (e.g. mid-year/EOY). P2P passes START_SURVEY_THEME for the START-blue accent.
   */
  surveyThemeVariables?: Record<string, string>
  /**
   * Suppresses the Previous/Continue/Submit navigation bar. The P2P collection auto-saves and
   * has no per-building submit — navigation is the wizard's job, not the form's.
   */
  hideNavigation?: boolean
}

/**
 * START-blue accent for SurveyJS native chrome. Defined as a stable module constant so
 * passing it as a prop doesn't churn onLoad's identity.
 */
export const START_SURVEY_THEME: Record<string, string> = {
  '--sjs-primary-backcolor': '#0067A5',
  '--sjs-primary-backcolor-dark': '#004975',
  '--sjs-primary-backcolor-light': 'rgba(0, 103, 165, 0.1)'
}

export interface STARTSurveyDisplayHandle {
  /** Persists a pending debounced autosave now; resolves immediately when nothing is pending. */
  flushPendingSave: () => Promise<void>
}

export const STARTSurveyDisplay = forwardRef<STARTSurveyDisplayHandle, Props>(function STARTSurveyDisplay ({
  annualPlanId,
  surveyType,
  title,
  navigateToSummary,
  planOrgId,
  onSurveyLoaded,
  initialSurvey,
  setSurvey,
  submitModalTitle,
  onExit,
  surveyVariables,
  loadOverride,
  saveOverride,
  readOnly,
  surveyThemeVariables,
  hideNavigation
}: Props, ref): JSX.Element {
  const [surveyJson, setSurveyJson] = useState<any>(initialSurvey != null ? JSON.parse(initialSurvey.content) : null)
  const [loading, setLoading] = useState<boolean>(initialSurvey == null)
  const [answers, setAnswers] = useState<Record<string, any> | undefined>(initialSurvey?.answers != null ? JSON.parse(initialSurvey.answers) : undefined)
  const [surveyModel, setSurveyModel] = useState<Model | null>(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [pageCompleteStatus, setPageCompleteStatus] = useState<boolean[]>([])
  const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([0]))
  const [priorityTaskIds, setPriorityTaskIds] = useState<Record<number, number | undefined>>({})
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (planOrgId == null || saveOverride != null || loadOverride != null) return
    void (async () => {
      const progress = await handleApiResponse(GetTrackingProgress(planOrgId))
      if (progress == null) return
      const map: Record<number, number | undefined> = {}
      progress.priorities.forEach(p => { map[p.priorityNumber] = p.planOrgTaskId })
      setPriorityTaskIds(map)
    })()
  }, [planOrgId])

  const linkContextValue: SurveyLinkContextValue = useMemo(() => ({
    rcnId: surveyVariables?.rcnId,
    planId: surveyVariables?.planId,
    planOrgId: surveyVariables?.planOrgId ?? planOrgId,
    priorityTaskIds
  }), [surveyVariables, planOrgId, priorityTaskIds])

  const extractQuestionMetadata = (question: QuestionWithMetadata, questionName: string, data: Record<string, any>): void => {
    if (question.extraInfo !== undefined) {
      data[questionName + SURVEY_COMMENT_SUFFIX] = (question.extraInfo != null && question.extraInfo !== '') ? question.extraInfo : null
    }
    if (question.uploadedFileName !== undefined) {
      data[questionName + SURVEY_FILE_SUFFIX] = (question.uploadedFileName != null && question.uploadedFileName !== '') ? question.uploadedFileName : null
    }
    if (question.uploadedFileKey !== undefined) {
      data[questionName + SURVEY_FILE_KEY_SUFFIX] = (question.uploadedFileKey != null && question.uploadedFileKey !== '') ? question.uploadedFileKey : null
    }
  }

  useEffect(() => {
    void loadSchema()
  }, [])

  const saveSurvey = useCallback(async (survey: Model, submit?: boolean): Promise<void> => {
    const data = survey.data
    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          // Guard the panel's answer slice: an untouched paneldynamic has no `data[name]`
          // yet, so `data[name][index]` would throw (the load path already guards this).
          const panelData = data[question.name]?.[index]
          if (panelData == null) return
          panel.questions.forEach((panelQuestion) => {
            extractQuestionMetadata(panelQuestion, panelQuestion.name, panelData)
          })
        })
      }

      if (hasMetadata(q)) {
        extractQuestionMetadata(question, q.name, data)
      }
    })

    const answersString = JSON.stringify(data)
    if (saveOverride != null) {
      await saveOverride(answersString, submit)
    } else {
      await SaveSurveyResults({
        schemaId: surveyType,
        surveyAnswers: answersString,
        planOrgId,
        submit
      })
    }

    if (setSurvey != null && submit === true) {
      setSurvey(prev => prev != null ? { ...prev, answers: answersString, isSubmitted: true } : undefined)
    }

    if (submit === true) {
      navigateToSummary?.()
    }
  }, [annualPlanId, surveyType, navigateToSummary, planOrgId, setSurvey, saveOverride])

  const debouncedSave = useCallback((): void => {
    if (surveyModel == null) return

    if (saveTimeoutRef.current != null) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = undefined
      void saveSurvey(surveyModel)
    }, AUTO_SAVE_DELAY)
  }, [surveyModel, saveSurvey])

  const takePendingSave = useCallback((): boolean => {
    if (saveTimeoutRef.current == null) return false
    clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = undefined
    return true
  }, [])

  const flushPendingSave = useCallback(async (): Promise<void> => {
    if (surveyModel == null || !takePendingSave()) return
    await saveSurvey(surveyModel)
  }, [surveyModel, saveSurvey, takePendingSave])

  useImperativeHandle(ref, () => ({ flushPendingSave }), [flushPendingSave])

  const loadQuestionMetadata = useCallback((question: QuestionWithMetadata, questionName: string, savedData: Record<string, any>, survey: Model): void => {
    if (savedData[questionName + SURVEY_COMMENT_SUFFIX] != null) {
      question.extraInfo = savedData[questionName + SURVEY_COMMENT_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_SUFFIX] != null) {
      question.uploadedFileName = savedData[questionName + SURVEY_FILE_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_KEY_SUFFIX] != null) {
      question.uploadedFileKey = savedData[questionName + SURVEY_FILE_KEY_SUFFIX]
    }
  }, [])

  const updatePageCompleteStatus = useCallback((model: Model, visited: Set<number>) => {
    const statuses = model.pages.map((page, index) => {
      if (!visited.has(index)) {
        return false
      }

      const allQuestions = page.getQuestions(true)
      return allQuestions.every(q => {
        const isActuallyRequired = q.isRequired && q.isVisible
        return !isActuallyRequired || !q.isEmpty()
      })
    })
    setPageCompleteStatus(statuses)
  }, [])

  const onComplete = useCallback(async (survey: Model, _options: any): Promise<void> => {
    await saveSurvey(survey, true)
  }, [saveSurvey])

  // Mutate the SurveyJS model synchronously at creation time (see SurveyDisplay's
  // useMemo) so the very first paint is already themed, panelless, title/nav hidden,
  // and answers-populated. Doing this in a post-render effect instead makes the survey
  // flash its unstyled default for a frame before snapping to the configured state.
  const configureModel = useCallback((survey: Model): void => {
    survey.questionErrorLocation = 'bottom'

    const appliedTheme = {
      isPanelless: true,
      cssVariables: {
        '--sjs-general-backcolor': 'transparent',
        '--sjs-general-backcolor-dim': 'transparent',
        '--sjs-paper-backcolor': 'transparent',
        '--sjs-paper-shadow': 'none',
        ...surveyThemeVariables
      }
    }

    survey.applyTheme(appliedTheme)
    if (readOnly === true) survey.mode = 'display'
    survey.widthMode = 'responsive'
    survey.showTitle = false
    survey.showQuestionNumbers = 'off'
    survey.showNavigationButtons = 'none'
    survey.showCompletedPage = false
    survey.checkErrorsMode = 'onComplete'
    survey.data = answers ?? {}

    if (surveyVariables != null) {
      Object.entries(surveyVariables).forEach(([key, value]) => {
        survey.setVariable(key, value)
      })

      survey.getAllQuestions().forEach(q => {
        if (q.getType() === 'surveytrackingcards') {
          if (surveyVariables.planOrganizationId != null) {
            (q as any).planOrganizationId = surveyVariables.planOrganizationId
          }
          const { rcnId, planId, planOrgId } = surveyVariables
          if (rcnId != null && planId != null && planOrgId != null) {
            (q as any).priorityBasePath = `/Implementation/${String(rcnId)}/${String(planId)}/3/PlanOrganization/${String(planOrgId)}/Priority`
          }
        }
      })
    }

    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata
      if (answers == null) return

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          if (answers[question.name]?.[index] == null) return

          panel.questions.forEach((panelQuestion) => {
            const savedData = answers[question.name][index]
            loadQuestionMetadata(panelQuestion, panelQuestion.name, savedData, survey)
          })
        })
      }

      if (hasMetadata(q)) {
        loadQuestionMetadata(question, question.name, answers, survey)
      }
    })

    survey.onUploadFiles.add((_sender: Model, { question, files, callback }: UploadFilesEvent) => {
      void Promise.all(files.map(async (file: File) => {
        try {
          const key = await UploadFile(file)
          const q = question as QuestionWithMetadata
          q.uploadedFileKey = key
          q.uploadedFileName = file.name
          return {
            file,
            content: key
          }
        } catch (e) {
          console.error('Failed to upload file', e)
          return undefined
        }
      })).then((results) => {
        const success = results.filter(r => r !== undefined)
        callback(success)
      })
    })
  }, [answers, loadQuestionMetadata, readOnly, surveyThemeVariables, surveyVariables])

  // React-state setup that must run after the model exists (enables the stepper and
  // nav buttons). The model itself is already configured by configureModel before the
  // first paint, so this no longer drives the survey's initial appearance.
  const onLoad = useCallback((survey: Model): void => {
    setSurveyModel(survey)

    const initialVisited = new Set<number>([0])
    survey.pages.forEach((page, index) => {
      const hasAnswers = page.getQuestions(true).some(q => !q.isEmpty())
      if (hasAnswers) {
        initialVisited.add(index)
      }
    })
    setVisitedPages(initialVisited)
    updatePageCompleteStatus(survey, initialVisited)
  }, [updatePageCompleteStatus])

  useEffect(() => {
    if (surveyModel == null) return

    const updatePage = (): void => {
      setCurrentPageIndex(surveyModel.currentPageNo)
    }

    surveyModel.onCurrentPageChanged.add(updatePage)
    return () => {
      surveyModel.onCurrentPageChanged.remove(updatePage)
    }
  }, [surveyModel])

  useEffect(() => {
    if (surveyModel == null) return

    const checkStatus = (): void => {
      updatePageCompleteStatus(surveyModel, visitedPages)
    }

    const markPageVisited = (): void => {
      setVisitedPages(prev => {
        if (prev.has(surveyModel.currentPageNo)) return prev
        return new Set(prev).add(surveyModel.currentPageNo)
      })
    }

    checkStatus()
    markPageVisited()

    surveyModel.onValueChanged.add(checkStatus)
    surveyModel.onValueChanged.add(debouncedSave)
    surveyModel.onCurrentPageChanged.add(checkStatus)
    surveyModel.onCurrentPageChanged.add(markPageVisited)
    return () => {
      surveyModel.onValueChanged.remove(checkStatus)
      surveyModel.onValueChanged.remove(debouncedSave)
      surveyModel.onCurrentPageChanged.remove(checkStatus)
      surveyModel.onCurrentPageChanged.remove(markPageVisited)
      if (takePendingSave()) void saveSurvey(surveyModel)
    }
  }, [surveyModel, visitedPages, updatePageCompleteStatus, debouncedSave, saveSurvey, takePendingSave])

  const loadSchema = async (): Promise<void> => {
    if (initialSurvey != null) {
      onSurveyLoaded?.(initialSurvey)
      setLoading(false)
      return
    }

    const survey = loadOverride != null
      ? await loadOverride()
      : await handleApiResponse(GetSurvey(surveyType, planOrgId))
    if (survey != null) {
      const schemaJson = JSON.parse(survey.content)
      setSurveyJson(schemaJson)
      if (survey.answers != null) setAnswers(JSON.parse(survey.answers))
      onSurveyLoaded?.(survey)
    }
    setLoading(false)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  const showStepper = surveyJson?.pages?.length > 1

  return (
    <SurveyLinkContext.Provider value={linkContextValue}>
      <Fade in timeout={250}>
        <Box className={surveyType === SurveyType.PeerToPeer ? 'startSurvey--p2p' : undefined} sx={{ width: '100%' }}>
          <Box sx={{ mt: 2, ml: 5 }}>
            <Typography variant="h3">{title}</Typography>
          </Box>
          {showStepper && surveyModel != null && (
            <SurveyStepper
              surveyModel={surveyModel}
              currentPageIndex={currentPageIndex}
              pageCompleteStatus={pageCompleteStatus}
            />
          )}
          {surveyJson !== null
            ? (
              <SurveyDisplay
                surveyJson={surveyJson}
                onComplete={onComplete}
                onLoad={onLoad}
                configureModel={configureModel}
              />
              )
            : <div>No schema found</div>
          }
          {surveyModel != null && hideNavigation !== true && (
            <SurveyNavigationButtons
              survey={surveyModel}
              saveSurvey={saveSurvey}
              submitModalTitle={submitModalTitle}
              onExit={onExit}
            />
          )}
        </Box>
      </Fade>
    </SurveyLinkContext.Provider>
  )
})
